import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	AlertTriangle,
	ArrowRight,
	BarChart3,
	CheckCircle2,
	ExternalLink,
	Eye,
	MousePointerClick,
	RefreshCw,
	Rocket,
	ShoppingBag,
	Sparkles,
	Store
} from 'lucide-react';

import { EmptyState } from '@/components/ui/empty-state';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { LocalPotentialHeader } from '@/components/overview/LocalPotentialHeader';
import { ActionCard } from '@/components/overview/ActionCard';
import { CenterStatusStrip } from '@/components/firstThirty/CenterStatusStrip';
import { QuickWinsStack } from '@/components/firstThirty/QuickWinsStack';
import { LaunchStorefrontModal, markStorefrontLaunched } from '@/components/lifecycle/LaunchStorefrontModal';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannels } from '@/hooks/useChannels';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useOrders } from '@/hooks/useOrders';
import { useStockSummary, useInventoryPageSummary } from '@/hooks/useInventory';
import { useGbpInsights, useLocalAds, useStorefrontInsights } from '@/hooks/useInsights';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useVerifyEmail } from '@/components/verification/VerifyEmailProvider';
import { useTrialStatus } from '@/lib/shop-status';
import { firstMeaningfulWord } from '@/lib/shop-name';
import { DISCONNECTED_THRESHOLD_DAYS, daysSince } from '@/lib/connection-state';

// "Active" orders are those still needing the retailer's attention — Pending
// (reserved/paid, awaiting confirmation) or Confirmed — i.e. not completed,
// cancelled or refunded. Drives the orders state of the "Today at a glance"
// action card. Matches the Orders page taxonomy (statuses are lower-case).
export const ACTIVE_ORDER_STATUSES = ['reserved', 'paid', 'confirmed'];

export const isActiveOrder = (order) => ACTIVE_ORDER_STATUSES.includes(String(order?.status).toLowerCase());

// An order's total lives on its line items, not a top-level field — mirrors the
// Orders page (`order.products[].price × quantity`).
export const orderTotal = (order) =>
	Array.isArray(order?.products) ? order.products.reduce((sum, p) => sum + (Number(p.price) || 0) * (Number(p.quantity) || 1), 0) : 0;

// Channels that aren't customer-visibility surfaces — excluded from the
// combined-visibility multiplier, matching the inventory page-summary's own
// `visibleCount` exclusion list.
const NON_VISIBILITY_CHANNELS = ['local-ads', 'webhook', 'product-locator'];

const channelEnabled = (channels, ...types) =>
	Array.isArray(channels) && channels.some((c) => types.includes(c.type) && (c.enabled === true || c.status === 'enabled'));

const slugify = (name) => (name ?? 'your-store').toLowerCase().trim().replace(/\s+/g, '-');

export function parseLatestIngest(raw) {
	if (!raw) return null;
	if (typeof raw === 'object') return raw;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

export function formatRelativeTime(iso) {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	if (!Number.isFinite(t)) return null;
	const minutes = Math.floor((Date.now() - t) / 60000);
	if (minutes < 1) return 'just now';
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
	const days = Math.floor(hours / 24);
	return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Number of days the POS has been silent for, gated on the same threshold the
 * Inventory page's `ConnectionStatusCard` uses (3 days, `DISCONNECTED_THRESHOLD_DAYS`).
 *
 * Returns `null` when:
 *   - the ingest is fresh (< 3 days),
 *   - the shop has never ingested at all (nothing to be stale yet), or
 *   - the POS source row has been removed (`hasEverIngested && !stockInventorySource`).
 *     This is the load-bearing suppression rule — the POS-disconnect case has
 *     its own banner via `usePosHealthBanner`, so flagging it here would
 *     double-warn (same suppression rule `useStaleIngestBanner` uses).
 *
 * Pure / table-testable so a regression in any of the four branches (fresh,
 * stale, disconnected-but-stale → null, no-ingest-yet) is caught by the unit
 * suite rather than slipping through the wider integration tests.
 */
export function computeInventoryStaleDays(shop, ingest) {
	if (shop?.hasEverIngested === true && !shop?.stockInventorySource) return null;
	const days = daysSince(ingest?.timestamp ?? ingest?.at);
	if (days == null || days < DISCONNECTED_THRESHOLD_DAYS) return null;
	return days;
}

/** 'none' | 'listings' | 'storefront' | 'both' — drives the celebration strip. */
export function deriveFirstThirtyStatus(storefrontLive, listingsLive) {
	if (storefrontLive && listingsLive) return 'both';
	if (storefrontLive) return 'storefront';
	if (listingsLive) return 'listings';
	return 'none';
}

// Mirrors fresh-dashboard's `getGreetingSub(LifecycleState)`. We pass an
// explicit `loading` flag because billing resolves async; rendering the
// `trial` copy on first paint and swapping to `paying` after billing
// returns visibly flashes "...is doing — and a few" → "...is doing today
// — and the few" on every page load for a paying shop. Returning an empty
// string while loading keeps the reserved `min-h-12` slot empty so the
// page below doesn't shift when the real copy lands.
export function getGreetingSub({ expired, hasPayment, loading }) {
	if (loading) return '';
	if (expired) return 'Your trial has ended. Reactivate to keep selling everywhere.';
	if (hasPayment) return "Here's how your store is doing today — and the few things worth a minute of your time.";
	return "Here's how your store is doing — and a few things worth a minute of your time.";
}

// Time-of-day greeting based on the user's local clock. Boundaries: before
// noon → morning, before 18:00 → afternoon, otherwise evening.
export function getGreeting(date = new Date()) {
	const hour = date.getHours();
	if (hour < 12) return 'Good morning';
	if (hour < 18) return 'Good afternoon';
	return 'Good evening';
}

// The gauge subtitle tracks the first-30-days window (anchored on the Stripe
// subscription start, mirroring the "Your first 30 days" section below), not
// payment state — a shop that converts to paying inside its first 30 days
// should still be nudged towards the 60% target.
export function getScoreSubtitle({ expired, isConverted, loading }) {
	if (loading) return '';
	if (expired) return 'Reactivate to keep improving';
	if (isConverted) return 'Keep improving — increase success';
	return 'Aim for 60% in your first 30 days';
}

/**
 * The real "what we've turned on for you" list, built from the data the
 * dashboard actually knows about. Reused by the onboarding "Launched" panel
 * and the results-area status cards. Returns relative hrefs — callers wrap
 * them in `useShopPath()`.
 */
export function buildAutomations({
	ingest,
	totalItems,
	inventoryLastSync,
	inventoryStaleDays,
	googleListingsLive,
	storefrontLive,
	listingsViews,
	channels,
	googleAllDisapproved
}) {
	// `inventoryStaleDays` is a positive integer when the POS hasn't ingested
	// for ≥ DISCONNECTED_THRESHOLD_DAYS, and `null` otherwise (fresh, no ingest
	// yet, or POS disconnected — that root cause owns its own banner). The
	// caller does the suppression via `computeInventoryStaleDays`, so this
	// function stays a pure renderer.
	const isStale = inventoryStaleDays != null;
	const staleSuffix = `Inventory hasn't synced in ${inventoryStaleDays} days · listings may be stale`;

	const googleDetail = () => {
		if (!googleListingsLive) return 'Submitted to Google · approval up to 10 days';
		// `allGmcDisapproved` is more specific + more actionable than the
		// catch-all "stale ingest" message and matches the precedence the
		// sibling `ChannelSyncStatus` card uses, so let it win when both fire.
		if (googleAllDisapproved) return 'Issues · all products disapproved by Google';
		if (isStale) return staleSuffix;
		if (listingsViews != null && listingsViews > 0) return `Live · ${listingsViews.toLocaleString('en-GB')} views in the last 30 days`;
		return 'Live on Google · Search, Maps and Images';
	};
	const inventoryDetail = () => {
		if (isStale) return `Last sync ${inventoryStaleDays} days ago · reconnect your POS to refresh`;
		if (totalItems == null) return 'Awaiting first sync';
		return `${totalItems.toLocaleString('en-GB')} items${inventoryLastSync ? ` · synced ${inventoryLastSync}` : ''}`;
	};
	const items = [
		{
			label: 'Inventory sync',
			detail: inventoryDetail(),
			ok: Boolean(ingest) && !isStale,
			warn: isStale,
			href: '/inventory'
		},
		{
			label: 'Local Listings on Google',
			detail: googleDetail(),
			ok: googleListingsLive && !googleAllDisapproved && !isStale,
			warn: googleListingsLive && (googleAllDisapproved || isStale),
			href: '/channels/google/manage'
		}
	];
	if (storefrontLive) {
		items.push({
			label: 'Local Storefront',
			detail: isStale ? staleSuffix : 'Public storefront unlocked · shoppers can search your stock',
			ok: !isStale,
			warn: isStale,
			href: '/channels/storefront/manage'
		});
	}
	if (channelEnabled(channels, 'local-ads')) {
		items.push({ label: 'Local Ads', detail: 'Running', ok: true, href: '/channels/local-ads/manage' });
	}
	if (channelEnabled(channels, 'meta-commerce', 'meta-commerce-feed')) {
		items.push({
			label: 'Meta Commerce',
			detail: isStale ? staleSuffix : 'Live · catalogue in sync',
			ok: !isStale,
			warn: isStale,
			href: '/channels/meta-commerce/manage'
		});
	}
	return items;
}

export function Overview() {
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { enable } = useChannelActions(shop?.id);
	const { orders } = useOrders(shop?.id);
	const { summary } = useStockSummary(shop?.id);
	const { summary: pageSummary } = useInventoryPageSummary(shop?.id);
	// Headline Google performance for the at-a-glance cards comes from the Google
	// Business Profile `gmb` insights — the same source the Results page reads
	// (`useGbpInsights`), so "Views" reconciles with the "See results" link and
	// the card's "Search, Maps and Images" label. Product local-listings
	// impressions are a separate, often-sparse surface that previously made this
	// read 0 even when the profile had plenty of views. Fixed 30-day window — the
	// cards always say "(30 days)", independent of the Results page range picker.
	const gbpRange = useMemo(() => {
		const iso = (d) => d.toISOString().slice(0, 10);
		const DAY = 24 * 60 * 60 * 1000;
		const to = new Date();
		const from = new Date(to.getTime() - 30 * DAY);
		const lookbackTo = new Date(from.getTime() - DAY);
		const lookbackFrom = new Date(lookbackTo.getTime() - 30 * DAY);
		return { from: iso(from), to: iso(to), lookbackFrom: iso(lookbackFrom), lookbackTo: iso(lookbackTo), days: 30 };
	}, []);
	const gbp = useGbpInsights(shop?.id, gbpRange);
	const googleViews = gbp.totals.profileViews;
	const hasGoogleInsights = gbp.hasData;
	// The "Today at a glance" Views + Engagements cards roll up every live
	// surface — not just Google. When Local Ads or the Storefront are on, their
	// numbers fold in alongside GBP (NEA-5851). Hooks are gated on the channel
	// being enabled so we don't fire empty SWR fetches for shops that don't
	// have them.
	const localAdsEnabled = channelEnabled(channels, 'local-ads');
	const storefrontEnabledForInsights = channelEnabled(channels, 'storefront');
	const { data: localAdsData } = useLocalAds(localAdsEnabled ? shop?.id : null);
	const storefrontInsights = useStorefrontInsights(storefrontEnabledForInsights ? shop?.id : null, gbpRange);
	const localAdsViews = Number(localAdsData?.summary?.impressions || 0);
	const localAdsClicks = Number(localAdsData?.summary?.clicks || 0);
	const storefrontProductViews = storefrontInsights.hasData ? storefrontInsights.productViews : 0;
	const storefrontEngagements = storefrontInsights.hasData ? storefrontInsights.shopPageClicks + storefrontInsights.reservationStarts : 0;
	const totalViews = gbp.totals.profileViews + localAdsViews + storefrontProductViews;
	const totalEngagements = gbp.totals.interactions + localAdsClicks + storefrontEngagements;
	const hasAnyInsights = gbp.hasData || localAdsViews > 0 || localAdsClicks > 0 || storefrontInsights.hasData;
	// Dynamic "Across …" copy enumerates only the surfaces that actually
	// contributed — so a Listings-only shop still reads "Across Google Search,
	// Maps and Images" exactly as before.
	const viewSources = [
		gbp.hasData ? 'Google Search, Maps and Images' : null,
		localAdsViews > 0 ? 'Local Ads' : null,
		storefrontProductViews > 0 ? 'your Local Storefront' : null
	].filter(Boolean);
	const engagementSources = [
		gbp.hasData ? 'Google' : null,
		localAdsClicks > 0 ? 'Local Ads' : null,
		storefrontEngagements > 0 ? 'your Local Storefront' : null
	].filter(Boolean);
	const formatList = (items) => {
		if (items.length === 0) return '';
		if (items.length === 1) return items[0];
		return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
	};
	const viewsSub = viewSources.length ? `Across ${formatList(viewSources)}` : 'Across Google Search, Maps and Images';
	const engagementsSub = engagementSources.length
		? `Clicks to your store from ${formatList(engagementSources)}`
		: 'Clicks to your store from Google';
	const { billing, isLoading: billingLoading } = useBillingStatus(shop?.id);
	const { requireVerifiedEmail } = useVerifyEmail();
	const trial = useTrialStatus();

	const [launchOpen, setLaunchOpen] = useState(false);

	const expired = trial?.expired === true;
	const hasPayment = trial?.hasPayment === true;
	// `useTrialStatus()` returns null while billing is loading OR when the
	// shop has no subscription — both cases render no subtitle, mirroring
	// fresh-dashboard's `default: ""` lifecycle branch. The min-h on the
	// <p> keeps the slot reserved so this doesn't shift the layout.
	const trialLoading = trial == null;
	// Show "Your First 30 Days" for 30 days from subscription start, regardless
	// of billing phase. Trial-based journeys use trialEnd (already 30 days);
	// variant-C (no trial) uses subscriptionStart + 30 days (NEA-5836).
	const thirtyDaysEnd =
		billing?.trialEnd ??
		(billing?.subscriptionStart
			? new Date(new Date(billing.subscriptionStart).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
			: null);
	const isConverted = thirtyDaysEnd != null && new Date(thirtyDaysEnd) < new Date();
	const pageLoading = billingLoading || channelsLoading;

	const googleListingsLive = channelEnabled(channels, 'google');
	const googleChannel = Array.isArray(channels) ? channels.find((c) => c.type === 'google' && c.status === 'enabled') : null;
	const gmcStats = googleChannel?.config?.productStatistics;
	const googleAllDisapproved = gmcStats?.active === 0 && gmcStats?.disapproved > 0;
	// Only the Storefront channel counts as "storefront live". Product
	// Reservations / Local Checkout are separate channels (they drive a Google
	// orders feed, not the near.st storefront), so enabling them must not make
	// the storefront read as live.
	const storefrontLive = channelEnabled(channels, 'storefront');
	const firstThirtyStatus = deriveFirstThirtyStatus(storefrontLive, googleListingsLive && !googleAllDisapproved);
	// When Google is also live ('both') the storefront card splits the centre column
	// 50/50 with the Google celebration strip, so it renders in a compact form.
	const bothLive = firstThirtyStatus === 'both';

	// The core-activator card flexes its height/padding: full 320px when it's the only
	// card in the centre column ('none'), compact when it splits the column with the
	// Google strip ('both'), else fills the top half above the strip.
	// All states pin the CTA to the card bottom via a flex-1 content block (the
	// both-live split deliberately deviates from the design's centred-flow layout
	// so its button lines up with the Google strip's below it).
	const storefrontHeroBase =
		'group relative flex flex-col overflow-hidden rounded-2xl border-0 bg-gradient-unify shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-1 hover:shadow-[0_20px_48px_-16px_hsl(var(--primary)/0.55)] active:translate-y-0';
	const storefrontHeroClass = `${storefrontHeroBase} ${firstThirtyStatus === 'none' ? 'p-6 md:h-[320px] md:p-7' : 'p-4 md:h-full md:p-4'}`;
	// Stacked above the Google strip the card is shorter, so clamp the body (matches
	// the design's `[&>a>div>p]:line-clamp-2`); the full 320px 'none' card has room.
	const heroBodyClamp = firstThirtyStatus === 'none' ? '' : 'line-clamp-2';

	// No live Google picture yet → keep the results area as a "coming soon"
	// preview rather than rendering empty KPIs.
	const isNew = !googleListingsLive;

	const ingest = parseLatestIngest(shop?.latestIngest ?? shop?.stockInventorySource?.latestIngest);
	const totalItems = summary?.totalItems ?? summary?.items ?? ingest?.items ?? ingest?.activeRecords ?? ingest?.stockIngested ?? null;
	const inventoryLastSync = formatRelativeTime(ingest?.at ?? ingest?.timestamp) ?? summary?.lastSync ?? null;
	const inventoryStaleDays = computeInventoryStaleDays(shop, ingest);

	const activeOrders = Array.isArray(orders) ? orders.filter(isActiveOrder) : [];
	const ordersToAction = activeOrders.length;
	const ordersTotalValue = activeOrders.reduce((sum, o) => sum + orderTotal(o), 0);
	const activeChannels = Array.isArray(channels)
		? channels.filter((c) => (c.enabled === true || c.status === 'enabled') && !NON_VISIBILITY_CHANNELS.includes(c.type)).length
		: 0;
	const visibleProducts = pageSummary?.visibility?.visibleCount ?? pageSummary?.inStockItems ?? totalItems;

	const automations = buildAutomations({
		ingest,
		totalItems,
		inventoryLastSync,
		inventoryStaleDays,
		googleListingsLive,
		storefrontLive,
		listingsViews: hasGoogleInsights ? googleViews : null,
		channels,
		googleAllDisapproved
	});
	const liveCount = automations.filter((a) => a.ok).length;
	const inProgressCount = automations.filter((a) => !a.ok && !a.warn).length;
	const hasWarnings = automations.some((a) => a.warn);

	const todayDate = new Date().toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric' });
	const greetingName = firstMeaningfulWord(shop?.name) || 'there';
	const storefrontUrl = `https://near.st/shop/${shop?.slug ?? slugify(shop?.name)}`;

	const openStorefront = () => window.open(storefrontUrl, '_blank', 'noopener,noreferrer');
	const onViewStorefront = (e) => {
		e.preventDefault();
		e.stopPropagation();
		openStorefront();
	};
	const onViewStorefrontKey = (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			e.stopPropagation();
			openStorefront();
		}
	};

	const renderAutomationRow = (a) => (
		<li key={a.label} className="flex items-center gap-4 py-4">
			{a.warn ? (
				<AlertTriangle className="h-5 w-5 shrink-0 text-warning-soft-foreground" />
			) : a.ok ? (
				<CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
			) : (
				<RefreshCw className="h-5 w-5 shrink-0 animate-spin text-action" style={{ animationDuration: '3s' }} />
			)}
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-foreground">{a.label}</div>
				<div className={`text-sm ${a.warn ? 'text-warning-soft-foreground' : 'text-muted-foreground'}`}>{a.detail}</div>
			</div>
			{a.warn ? (
				<span className="rounded-full bg-warning-soft px-2.5 py-1 text-xs font-semibold text-warning-soft-foreground">Issues</span>
			) : a.ok ? (
				<span className="rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-soft-foreground">Live</span>
			) : (
				<span className="rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold text-action-soft-foreground">
					In progress
				</span>
			)}
			<Link
				to={shopPath(a.href)}
				className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground transition hover:bg-secondary/70"
			>
				Manage
			</Link>
		</li>
	);

	return (
		<div className="space-y-10">
			<header data-tour="overview-kpis">
				<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{todayDate}</p>
				<div className="mt-1 flex items-start justify-between gap-4">
					<div className="min-w-0">
						<h1 className="text-3xl font-extrabold tracking-tight md:text-[34px]">
							{getGreeting()}, <span className="text-gradient-unify">{greetingName}</span>.
						</h1>
						<p className="mt-2 min-h-12 max-w-2xl text-base text-muted-foreground">
							{getGreetingSub({ expired, hasPayment, loading: trialLoading })}
						</p>
					</div>
					<div className="shrink-0">
						<LocalPotentialHeader animate subtitle={getScoreSubtitle({ expired, isConverted, loading: trialLoading })} />
					</div>
				</div>
			</header>

			{pageLoading ? (
				<>
					<section>
						<Skeleton className="mb-2 h-4 w-40" />
						<Skeleton className="mb-1 h-7 w-80" />
						<Skeleton className="h-4 w-64" />
						<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
							<Skeleton className="h-[320px] rounded-2xl" />
							<Skeleton className="h-[320px] rounded-2xl" />
							<Skeleton className="h-[320px] rounded-2xl" />
						</div>
					</section>
					<section>
						<Skeleton className="mb-2 h-4 w-36" />
						<Skeleton className="mb-4 h-7 w-56" />
						<Skeleton className="h-[200px] rounded-2xl" />
					</section>
				</>
			) : !isConverted ? (
				<section data-tour="overview-setup">
					<SectionHeader
						eyebrow="YOUR FIRST 30 DAYS"
						title="Key actions to win locally with Unify!"
						sub="Quick wins to unlock your Local Potential from day one."
					/>
					<div data-tour="quick-wins" className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
						{/* Column 1 — what's launched */}
						<div className="flex flex-col rounded-2xl border border-border/60 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:h-[320px] md:p-6">
							<div className="flex items-center gap-3 border-b border-border/60 pb-4">
								<span className="relative flex h-2.5 w-2.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
									<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
								</span>
								<span className="text-sm font-semibold text-foreground">Launched</span>
								<span className="ml-auto text-xs tabular-nums text-muted-foreground">
									{liveCount} live{inProgressCount > 0 ? ` · ${inProgressCount} in progress` : ''}
								</span>
							</div>
							<div className="relative min-h-0 flex-1">
								<ul className="-mr-2 h-full divide-y divide-border/60 overflow-y-auto pr-2">
									{automations.map((a) => (
										<li key={a.label} className="flex items-center gap-3 py-3.5">
											{a.warn ? (
												<AlertTriangle className="h-5 w-5 shrink-0 text-warning-soft-foreground" />
											) : a.ok ? (
												<CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
											) : (
												<RefreshCw
													className="h-5 w-5 shrink-0 animate-spin text-action"
													style={{ animationDuration: '3s' }}
												/>
											)}
											<div className="min-w-0 flex-1">
												<div className="text-sm font-semibold text-foreground">{a.label}</div>
												<div
													className={`truncate text-xs ${a.warn ? 'text-warning-soft-foreground' : 'text-muted-foreground'}`}
												>
													{a.detail}
												</div>
											</div>
											{a.warn ? (
												<span className="rounded-full bg-warning-soft px-2.5 py-1 text-xs font-semibold text-warning-soft-foreground">
													Issues
												</span>
											) : a.ok ? (
												<span className="rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-soft-foreground">
													Live
												</span>
											) : (
												<span className="rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold text-action-soft-foreground">
													In progress
												</span>
											)}
										</li>
									))}
								</ul>
								<div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 rounded-b-2xl bg-gradient-to-t from-white to-transparent" />
							</div>
						</div>

						{/* Column 2 — core activator: storefront card + Google Local Listings status */}
						<div className="md:h-[320px]">
							<div
								className={
									firstThirtyStatus === 'none'
										? 'md:h-full'
										: bothLive
											? 'grid h-full grid-cols-1 gap-3 md:grid-rows-2'
											: 'flex h-full flex-col gap-3'
								}
							>
								<div className={firstThirtyStatus === 'none' ? 'md:h-full' : bothLive ? 'min-h-0' : 'min-h-0 flex-1'}>
									{storefrontLive ? (
										<Link to={shopPath('/channels/storefront/manage')} className={storefrontHeroClass}>
											<div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
											{bothLive ? (
												<>
													<div className="relative flex flex-1 items-start gap-3">
														<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/25">
															<Store className="h-5 w-5" />
														</span>
														<div className="min-w-0 flex-1">
															<h3 className="text-sm font-bold leading-tight text-white md:text-[15px]">
																Your Local Storefront is live!
															</h3>
															<p className="mt-1 line-clamp-2 text-xs text-white/80">
																Local shoppers can now search what you have in store.
															</p>
														</div>
													</div>
													<div className="relative mt-3 flex pl-[52px]">
														<span
															role="button"
															tabIndex={0}
															onClick={onViewStorefront}
															onKeyDown={onViewStorefrontKey}
															aria-label="Open storefront in new tab"
															className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
														>
															<ExternalLink className="h-3.5 w-3.5" /> View Local Storefront
														</span>
													</div>
												</>
											) : (
												<>
													<div className="relative flex-1">
														{/* No icon here — matches the design's live-storefront / Google-pending card. */}
														<div className="mb-4 flex items-center justify-between gap-2">
															<span />
														</div>
														<h3 className="text-lg font-bold leading-tight text-white md:text-[19px]">
															Your Local Storefront is live!
														</h3>
														<p className={`mt-1.5 text-sm text-white/75 ${heroBodyClamp}`}>
															Local shoppers can now search what you have in store. Turn on Reservations or
															Checkout so they can buy too.
														</p>
													</div>
													<div className="relative mt-5 flex">
														<span
															role="button"
															tabIndex={0}
															onClick={onViewStorefront}
															onKeyDown={onViewStorefrontKey}
															aria-label="Open storefront in new tab"
															className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
														>
															<ExternalLink className="h-3.5 w-3.5" /> View Local Storefront
														</span>
													</div>
												</>
											)}
										</Link>
									) : (
										<Link
											to={shopPath('/channels')}
											aria-label="Launch your storefront"
											data-tour="launch-storefront"
											onClick={(e) => {
												e.preventDefault();
												requireVerifiedEmail(() => setLaunchOpen(true));
											}}
											className={storefrontHeroClass}
										>
											<div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
											<div className="relative flex-1">
												<div className="mb-4 flex items-center justify-between gap-2">
													<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/25">
														<Store className="h-5 w-5" />
													</span>
													<span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">
														<Sparkles className="h-3 w-3" /> 1-click launch
													</span>
												</div>
												<h3 className="text-lg font-bold leading-tight text-white md:text-[19px]">
													Unlock your Local Storefront
												</h3>
												<p className={`mt-1.5 text-sm text-white/75 ${heroBodyClamp}`}>
													Turn on your searchable Local Storefront so local shoppers can find what you have in
													store.
												</p>
											</div>
											<div className="relative mt-5 flex">
												<span className="inline-flex w-full items-center justify-between gap-6 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary transition hover:bg-white/90">
													<span className="inline-flex items-center gap-2">
														<Rocket className="h-4 w-4" /> Launch Local Storefront
													</span>
													<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
												</span>
											</div>
										</Link>
									)}
								</div>
								{firstThirtyStatus !== 'none' ? (
									<CenterStatusStrip status={firstThirtyStatus} googleDisapproved={googleAllDisapproved} />
								) : null}
							</div>
						</div>

						{/* Column 3 — quick wins */}
						<QuickWinsStack />
					</div>
				</section>
			) : null}

			{isNew ? (
				<>
					<section>
						<EmptyState
							icon={BarChart3}
							eyebrow="Your store, soon"
							title="A live picture of your results — coming soon"
							body="Once your first results come in, this dashboard fills with the numbers that matter: views, clicks, orders to action and the channels working hardest for you."
							previewTitle="What you'll see at a glance"
							preview={[
								{ icon: Eye, label: 'Views across Google & Maps', detail: 'Updated daily' },
								{ icon: MousePointerClick, label: 'Clicks & engagements', detail: 'Calls, directions, product views' },
								{ icon: ShoppingBag, label: 'Orders awaiting fulfilment', detail: 'Confirm & complete in one tap' }
							]}
						/>
					</section>
					<section>
						<SectionHeader
							eyebrow="Behind the scenes"
							title="Unify is working for you"
							sub="We've already started getting things ready in the background."
						/>
						<SoftCard tone="white">
							<div className="flex items-center gap-3 border-b border-border/60 pb-5">
								<span className="relative flex h-2.5 w-2.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
									<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
								</span>
								<span className="text-sm font-semibold text-foreground">Getting things ready</span>
								<span className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
									<RefreshCw className="h-3.5 w-3.5" /> Last checked just now
								</span>
							</div>
							<ul className="divide-y divide-border/60">{automations.map(renderAutomationRow)}</ul>
						</SoftCard>
					</section>
				</>
			) : (
				<>
					<section>
						<SectionHeader eyebrow="Today at a glance" title="Your store, right now" />
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
							<SoftCard tone="soft">
								<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Views (30 days)</div>
								<div className="mt-3 text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
									{hasAnyInsights ? totalViews.toLocaleString('en-GB') : '—'}
								</div>
								<div className="mt-2 text-sm text-muted-foreground">{viewsSub}</div>
								<Link
									to={shopPath('/results')}
									className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
								>
									See results <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</SoftCard>

							<SoftCard>
								<div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
									Engagements (30 days)
								</div>
								<div className="mt-3 text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
									{hasAnyInsights ? totalEngagements.toLocaleString('en-GB') : '—'}
								</div>
								<div className="mt-2 text-sm text-muted-foreground">{engagementsSub}</div>
							</SoftCard>

							<ActionCard
								ordersToAction={ordersToAction}
								ordersTotalValue={ordersTotalValue}
								visibleProducts={visibleProducts}
								activeChannels={activeChannels}
								currency={shop?.currency || 'GBP'}
							/>
						</div>
					</section>

					<section>
						<SectionHeader
							eyebrow="Behind the scenes"
							title="Unify is working for you"
							sub="Everything's running quietly in the background. Nothing for you to do."
						/>
						<SoftCard tone="white">
							<div className="flex items-center gap-3 border-b border-border/60 pb-5">
								{hasWarnings ? (
									<>
										<AlertTriangle className="h-4 w-4 shrink-0 text-warning-soft-foreground" />
										<span className="text-sm font-semibold text-foreground">Needs attention</span>
									</>
								) : (
									<>
										<span className="relative flex h-2.5 w-2.5">
											<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
											<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
										</span>
										<span className="text-sm font-semibold text-foreground">All systems live</span>
									</>
								)}
								<span className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
									<RefreshCw className="h-3.5 w-3.5" /> Last checked just now
								</span>
							</div>
							<ul className="divide-y divide-border/60">{automations.map(renderAutomationRow)}</ul>
							<div className="mt-1 flex justify-end border-t border-border/60 pt-5">
								<Link
									to={shopPath('/channels')}
									className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
								>
									Add more channels <ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</div>
						</SoftCard>
					</section>
				</>
			)}

			<LaunchStorefrontModal
				open={launchOpen}
				onClose={() => setLaunchOpen(false)}
				onActivated={() => {
					// Enable the storefront channel for real — useChannels revalidates,
					// so `storefrontLive` flips from live per-shop channel data (no local
					// flag needed). Mirrors the Channels page's first-channel handler.
					// Failures are swallowed here: the celebration shouldn't be chased by
					// a red error toast (the Channels page reflects the true channel state
					// on revalidation if the enable didn't land).
					enable('storefront').catch(() => {});
					markStorefrontLaunched();
				}}
			/>
		</div>
	);
}
